import React, { useEffect, useState } from 'react';
import { Search, Menu, X, ChevronDown, ChevronRight, User, ShoppingBag, Phone, Truck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const productCategories = [{
    name: 'Identification Labels',
    href: '#',
    subItems: [{
      name: 'Asset Labels',
      href: '/products/asset-labels'
    }, {
      name: 'Asset Labels - Micro QR',
      href: '/products/asset-labels-micro-qr'
    }, {
      name: 'Tamper Evident & Security Labels',
      href: '/products/tamper-evident-labels'
    }, {
      name: 'Logo/Brand Labels',
      href: '/products/logo-brand-labels'
    }]
  }, {
    name: 'Nameplates',
    href: '/products/nameplates'
  }];
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white'}`} data-id="element-135">
      {/* Top Announcement Bar */}
      <div className="bg-[#0A1628] text-white py-2.5" data-id="element-136">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-137">
          {/* Left - Free delivery message */}
          <div className="flex items-center gap-2 text-sm font-semibold tracking-wide" data-id="element-138">
            <Truck className="h-4 w-4 text-[#0052CC]" data-id="element-139" />
            <span data-id="element-140">Free nationwide delivery on all orders</span>
          </div>

          {/* Center - Heritage text */}
          <div className="hidden md:flex items-center gap-2 text-sm font-medium text-slate-300" data-id="element-141">
            <span data-id="element-142">🇬🇧</span>
            <span data-id="element-143">Family owned UK manufacturer, Est. 2009</span>
          </div>

          {/* Right - Phone number */}
          <a href="tel:01384242426" className="hidden sm:flex items-center gap-2 text-sm font-bold hover:text-white text-slate-200 transition-colors" data-id="element-144">
            <Phone className="h-4 w-4" data-id="element-145" />
            <span data-id="element-146">01384 242426</span>
          </a>
        </div>
      </div>

      {/* Main Header */}
      <div className="border-b border-slate-100" data-id="element-147">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-4" data-id="element-148">
          <div className="flex items-center justify-between gap-8" data-id="element-149">
            {/* Logo */}
            <a href="/" className="flex-shrink-0" data-id="element-150">
              <img src="https://cdn.magicpatterns.com/uploads/q9rehXWKJNRy1KSyHWnt8x/ILN_Logo.png" alt="Industrial Labels & Nameplates" className="h-14 md:h-16 w-auto object-contain" data-id="element-151" />
            </a>

            {/* Desktop Navigation - Pills style */}
            <nav className="hidden lg:flex items-center bg-slate-100 rounded-full px-1.5 py-1.5" data-id="element-152">
              <div className="relative" onMouseEnter={() => setActiveDropdown('products')} onMouseLeave={() => setActiveDropdown(null)} data-id="element-153">
                <button className="flex items-center text-sm font-semibold text-[#0A1628] bg-white hover:bg-[#0052CC] hover:text-white px-4 py-2 rounded-full transition-all shadow-sm" data-id="element-154">
                  Products
                  <ChevronDown className="ml-1 h-3.5 w-3.5" data-id="element-155" />
                </button>

                <AnimatePresence data-id="element-156">
                  {activeDropdown === 'products' && <motion.div initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 8
                }} transition={{
                  duration: 0.15
                }} className="absolute top-full left-0 mt-2 w-72 bg-white shadow-xl border border-slate-200 rounded-xl overflow-hidden p-2 z-50" data-id="element-157">
                      {productCategories.map(category => <div key={category.name} data-id="element-158">
                          {category.subItems ? <div data-id="element-159">
                              <div className="flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-[#0A1628] bg-slate-50 rounded-lg mb-1" data-id="element-160">
                                {category.name}
                                <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-161" />
                              </div>
                              <div className="pl-3 space-y-0.5 mb-2" data-id="element-162">
                                {category.subItems.map(subItem => <a key={subItem.name} href={subItem.href} className="block px-3 py-2 text-sm text-slate-600 hover:text-[#0052CC] hover:bg-[#0052CC]/5 rounded-lg transition-colors" data-id="element-163">
                                    {subItem.name}
                                  </a>)}
                              </div>
                            </div> : <a href={category.href} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:text-[#0052CC] hover:bg-slate-50 rounded-lg transition-colors" data-id="element-164">
                              {category.name}
                            </a>}
                        </div>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              {['Sectors', 'Resources', 'About'].map(item => <a key={item} href="#" className="text-sm font-medium text-slate-600 hover:text-[#0A1628] px-4 py-2 rounded-full transition-colors" data-id="element-165">
                  {item}
                </a>)}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-3" data-id="element-166">
              {/* Search */}
              <div className="hidden md:flex" data-id="element-167">
                <div className="relative" data-id="element-168">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-169" />
                  <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search products..." className="w-56 pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-full text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] transition-all" data-id="element-170" />
                </div>
              </div>

              {/* Mobile Search Icon */}
              <button className="md:hidden p-2.5 text-slate-600 hover:text-[#0A1628] hover:bg-slate-100 rounded-full transition-colors" data-id="element-171">
                <Search className="h-5 w-5" data-id="element-172" />
              </button>

              {/* User Account */}
              <a href="/account" className="p-2.5 text-slate-600 hover:text-[#0A1628] hover:bg-slate-100 rounded-full transition-colors" title="My Account" data-id="element-173">
                <User className="h-5 w-5" data-id="element-174" />
              </a>

              {/* Basket */}
              <a href="/basket" className="relative p-2.5 text-slate-600 hover:text-[#0A1628] hover:bg-slate-100 rounded-full transition-colors" title="Basket" data-id="element-175">
                <ShoppingBag className="h-5 w-5" data-id="element-176" />
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-[#0052CC] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-id="element-177">
                  0
                </span>
              </a>

              {/* Mobile Menu Button */}
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2.5 text-slate-700 hover:bg-slate-100 rounded-full transition-colors" data-id="element-178">
                {isMobileMenuOpen ? <X className="h-5 w-5" data-id="element-179" /> : <Menu className="h-5 w-5" data-id="element-180" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-181">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="fixed inset-0 top-[125px] bg-white z-40 px-6 py-6 overflow-y-auto lg:hidden border-t border-slate-100" data-id="element-182">
            <div className="flex flex-col space-y-1" data-id="element-183">
              {/* Mobile Search */}
              <div className="mb-4" data-id="element-184">
                <div className="relative" data-id="element-185">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-186" />
                  <input type="text" placeholder="Search products..." className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]" data-id="element-187" />
                </div>
              </div>

              {/* Products with sub-items */}
              <div className="border-b border-slate-100 pb-3 mb-2" data-id="element-188">
                <span className="text-lg font-semibold text-slate-900 py-3 block" data-id="element-189">
                  Products
                </span>
                <div className="pl-4 space-y-2" data-id="element-190">
                  <span className="text-sm font-semibold text-slate-500 block pt-2" data-id="element-191">
                    Identification Labels
                  </span>
                  {productCategories[0].subItems?.map(item => <a key={item.name} href={item.href} className="text-base text-slate-700 py-2 block pl-2" data-id="element-192">
                      {item.name}
                    </a>)}
                  <a href="/products/nameplates" className="text-base font-medium text-slate-900 py-2 block pt-3" data-id="element-193">
                    Nameplates
                  </a>
                </div>
              </div>

              {['Sectors', 'Resources', 'About', 'Contact'].map(item => <a key={item} href="#" className="text-lg font-medium text-slate-900 py-3 border-b border-slate-100" data-id="element-194">
                  {item}
                </a>)}

              {/* Mobile phone number */}
              <a href="tel:01384242426" className="flex items-center gap-2 text-lg font-medium text-slate-900 py-3 border-b border-slate-100" data-id="element-195">
                <Phone className="h-5 w-5" data-id="element-196" />
                01384 242426
              </a>

              <div className="pt-6 space-y-3" data-id="element-197">
                <a href="/products" className="block w-full py-3.5 bg-[#0052CC] text-white text-center font-bold rounded-xl" data-id="element-198">
                  Browse products
                </a>
                <a href="/samples" className="block w-full py-3.5 bg-slate-100 text-slate-700 text-center font-semibold rounded-xl" data-id="element-199">
                  Request a sample pack
                </a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}